import { createContext, useContext } from 'react';
import { Plus, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { buildDefaultScale, gradingOf, withRanks, type GradingScale, type SectionGrading } from '../lib/grading-scale';

export const DocumentScalesContext = createContext<GradingScale[]>([]);

interface Props {
  bareme: number | null;
  grading: SectionGrading;
  editable: boolean;
  onBareme: (v: number | null) => void;
  onGrading: (g: SectionGrading) => void;
}

const NEW_SCALE = '__new__';

export function GradingField({ bareme, grading, editable, onBareme, onGrading }: Props) {
  const documentScales = useContext(DocumentScalesContext);
  const scale = grading.mode === 'scale' ? grading.scale : null;
  const options = scale && !documentScales.some((s) => s.id === scale.id) ? [...documentScales, scale] : documentScales;

  const setScale = (next: GradingScale) => onGrading({ mode: 'scale', scaleId: next.id, scale: next });
  const pickScale = (id: string) => {
    if (id === NEW_SCALE) return setScale(buildDefaultScale(crypto.randomUUID()));
    const found = options.find((s) => s.id === id);
    if (found) setScale(found);
  };
  const setLevel = (index: number, patch: { code?: string; label?: string }) =>
    scale && setScale({ ...scale, levels: scale.levels.map((l, i) => (i === index ? { ...l, ...patch } : l)) });
  const moveLevel = (index: number, dir: -1 | 1) => {
    if (!scale) return;
    const target = index + dir;
    if (target < 0 || target >= scale.levels.length) return;
    const levels = [...scale.levels];
    [levels[index], levels[target]] = [levels[target]!, levels[index]!];
    setScale({ ...scale, levels: withRanks(levels) });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <NativeSelect
          aria-label="Mode de notation"
          disabled={!editable}
          value={grading.mode}
          onChange={(e) => {
            if (e.target.value === 'numeric') onGrading({ mode: 'numeric' });
            else pickScale(options[0]?.id ?? NEW_SCALE);
          }}
          className="h-7 text-[12px] py-0 w-auto"
        >
          <option value="numeric">Points</option>
          <option value="scale">Lettres</option>
        </NativeSelect>
        {grading.mode === 'numeric' && (
          <>
            <Input type="number" min={0} step={0.5} disabled={!editable} value={bareme ?? ''} placeholder="—"
              onChange={(e) => onBareme(e.target.value === '' ? null : Number(e.target.value))} className="w-[72px] h-7 text-[12px] py-0" />
            <span className="text-[12px] text-[var(--naia-text-secondary)] font-medium">pts</span>
          </>
        )}
        {scale && (
          <NativeSelect aria-label="Échelle" disabled={!editable} value={scale.id} onChange={(e) => pickScale(e.target.value)} className="h-7 text-[12px] py-0 w-auto">
            {options.map((s) => (
              <option key={s.id} value={s.id}>{s.label || 'Échelle sans nom'}</option>
            ))}
            <option value={NEW_SCALE}>+ Nouvelle échelle</option>
          </NativeSelect>
        )}
      </div>
      {scale && editable && (
        <div className="flex flex-col gap-1 rounded-md border border-border p-2">
          <Input aria-label="Nom de l’échelle" value={scale.label} onChange={(e) => setScale({ ...scale, label: e.target.value })} className="h-7 text-[12px] py-0" />
          {scale.levels.map((level, i) => (
            <div key={i} className="flex items-center gap-1">
              <Input aria-label="Code" value={level.code} onChange={(e) => setLevel(i, { code: e.target.value })} className="w-[72px] h-7 text-[12px] py-0" />
              <Input aria-label="Libellé" value={level.label} onChange={(e) => setLevel(i, { label: e.target.value })} className="flex-1 min-w-0 h-7 text-[12px] py-0" />
              <Button type="button" variant="ghost" size="icon" aria-label="Monter" disabled={i === 0} onClick={() => moveLevel(i, -1)} className="w-7 h-7 min-h-0">↑</Button>
              <Button type="button" variant="ghost" size="icon" aria-label="Descendre" disabled={i === scale.levels.length - 1} onClick={() => moveLevel(i, 1)} className="w-7 h-7 min-h-0">↓</Button>
              {scale.levels.length > 2 && (
                <Button type="button" variant="ghost" size="icon" aria-label="Retirer" onClick={() => setScale({ ...scale, levels: withRanks(scale.levels.filter((_, j) => j !== i)) })} className="w-7 h-7 min-h-0"><X size={12} /></Button>
              )}
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={() => setScale({ ...scale, levels: withRanks([...scale.levels, { code: '', label: '', rank: 0, level4: null }]) })} className="self-start h-7 text-[12px]">
            <Plus size={12} /> Ajouter un niveau
          </Button>
        </div>
      )}
    </div>
  );
}

export { gradingOf };
